import { Accordion, Box, Text, VStack } from '@chakra-ui/react'
import type { ContactAnalyticsFlag, ImpactScore } from '../../types/contactDetail'
import { flagLabel } from '../../types/analyticsFlags'

interface FlagLegendProps {
  flags: ContactAnalyticsFlag[]
  impactScores?: ImpactScore[]
}

/**
 * MQ-9 / DT-12: analytic flags render as chips exactly ONCE, at the name level
 * (ContactProfileHeader). This is the single explanation surface for them — one row per
 * flag, plain text (no second chip), plus which impact scores the flag contributes to.
 *
 * Rendered by ContactProfileHeader (not by ContactDetailPage) so the contact detail MODAL,
 * which shows the header without ImpactSection, gets the explanation too — the name-level
 * tooltip is hover-only and unreachable on touch.
 *
 * Collapsed by default at EVERY width, so it never pushes the contact's own data down.
 * Colours are mode-aware chrome tokens only (ink / ink.2 / muted on surface) — no raw ramp
 * steps, so no reflected-ramp double-invert in dark mode.
 */
export function FlagLegend({ flags, impactScores }: FlagLegendProps) {
  if (flags.length === 0) return null

  const computedAt = new Date(flags[0].computedAt)

  return (
    <Accordion.Root collapsible mt={3}>
      <Accordion.Item value="flag-legend" border="none">
        <Accordion.ItemTrigger px={0} css={{ mdDown: { minH: '2.75rem' } }}>
          <Box flex="1" textAlign="left">
            <Text fontSize="sm" fontWeight={600} color="ink">
              What these flags mean ({flags.length})
            </Text>
          </Box>
          <Accordion.ItemIndicator />
        </Accordion.ItemTrigger>
        <Accordion.ItemContent>
          <Accordion.ItemBody px={0} pb={2}>
            <VStack align="stretch" gap={3}>
              {flags.map((flag) => {
                const countsToward = (impactScores ?? [])
                  .filter((s) => s.contributingFlagKeys.includes(flag.flagKey))
                  .map((s) => s.label)
                return (
                  <Box key={flag.flagKey}>
                    <Text fontSize="sm" fontWeight={600} color="ink">{flagLabel(flag.flagKey)}</Text>
                    <Text fontSize="sm" color="ink.2">{flag.explanation}</Text>
                    {countsToward.length > 0 && (
                      <Text fontSize="xs" color="muted" mt={0.5}>
                        Counts toward: {countsToward.join(' · ')}
                      </Text>
                    )}
                  </Box>
                )
              })}
            </VStack>
            <Text fontSize="xs" color="muted" mt={4}>
              Flags computed {computedAt.toLocaleDateString()}
            </Text>
          </Accordion.ItemBody>
        </Accordion.ItemContent>
      </Accordion.Item>
    </Accordion.Root>
  )
}
